import { useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { getItemRatings, submitRating } from '../api';
import { useAuth } from '../auth';
import StarRating from './StarRating';

// fix_issue3.md #3 - approved ratings for a listing, plus a submit form for a logged-in customer.
// The backend only accepts a rating from someone with a completed rental of this item, and holds
// it for admin approval - so a 403 here is expected/normal, not an error to shout about.
export default function ItemRatings({ itemId }) {
  const { isAuthenticated } = useAuth();
  const [ratings, setRatings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [stars, setStars] = useState(0);
  const [review, setReview] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getItemRatings(itemId)
      .then((data) => { if (!cancelled) setRatings(data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [itemId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    if (!stars) { setErr('Pick a star rating first.'); return; }
    setSubmitting(true);
    try {
      const res = await submitRating(itemId, { rating: stars, review });
      setMsg(res.msg || 'Thanks - your rating will appear once it is reviewed.');
      setStars(0);
      setReview('');
    } catch (e2) {
      setErr(e2.response?.data?.msg || 'Unable to submit your rating.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="mt-4">
      <h5>Ratings &amp; reviews</h5>
      {ratings.length === 0 && <p className="text-muted small">No approved ratings yet.</p>}
      {ratings.map((r) => (
        <div key={r._id} className="border-bottom py-2">
          <StarRating value={r.rating} />
          <div className="small">
            {r.review && <span>{r.review}</span>}
            <span className="text-muted ms-2">
              — {r.userName || 'Verified renter'}, {new Date(r.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      ))}

      {isAuthenticated && (
        <Form onSubmit={handleSubmit} className="mt-3 border rounded p-3" style={{ maxWidth: 480 }}>
          <div className="mb-2">
            <Form.Label className="d-block small mb-1">Your rating</Form.Label>
            <StarRating value={stars} onRate={setStars} size="1.5rem" />
          </div>
          <Form.Group className="mb-2">
            <Form.Label className="small">Review (optional)</Form.Label>
            <Form.Control as="textarea" rows={2} maxLength={1000} value={review} onChange={(e) => setReview(e.target.value)} />
          </Form.Group>
          {msg && <Alert variant="success" className="py-2 mb-2">{msg}</Alert>}
          {err && <Alert variant="danger" className="py-2 mb-2">{err}</Alert>}
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit rating'}
          </Button>
          <Form.Text className="d-block text-muted mt-1">
            You can rate an item after a completed rental. Ratings appear once a website admin approves them.
          </Form.Text>
        </Form>
      )}
    </div>
  );
}
